import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, Clock, ChefHat, Package, Truck, XCircle, Copy } from 'lucide-react';
import TopBar from '../components/common/TopBar';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import { orderService, paymentService } from '../services/api';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';

const STATUS_FLOW = [
  { key: 'created', label: 'Criado', icon: Clock },
  { key: 'payment_pending', label: 'Aguardando pagamento', icon: Clock },
  { key: 'payment_approved', label: 'Pagamento aprovado', icon: CheckCircle2 },
  { key: 'received', label: 'Recebido pelo quiosque', icon: Package },
  { key: 'accepted', label: 'Aceito', icon: CheckCircle2 },
  { key: 'preparing', label: 'Em preparo', icon: ChefHat },
  { key: 'out_for_delivery', label: 'Saiu para entrega', icon: Truck },
  { key: 'delivered', label: 'Entregue', icon: CheckCircle2 },
];

const OrderTracking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadOrder();
    const interval = setInterval(loadOrder, 5000);
    return () => clearInterval(interval);
  }, [id]);

  const loadOrder = async () => {
    try {
      const orderData = await orderService.getById(id);
      setOrder(orderData);

      try {
        const paymentData = await paymentService.getByOrder(id);
        setPayment(paymentData);
      } catch (e) {
        // Payment not created yet
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar pedido');
    } finally {
      setLoading(false);
    }
  };

  const handleMockApprove = async () => {
    try {
      await paymentService.mockApprove(id);
      toast.success('Pagamento aprovado!');
      loadOrder();
    } catch (err) {
      toast.error('Erro ao aprovar pagamento');
    }
  };

  const handleCopyPix = () => {
    if (payment?.pix_code) {
      navigator.clipboard.writeText(payment.pix_code);
      toast.success('Código Pix copiado!');
    }
  };

  if (loading) return <LoadingSpinner size="lg" text="Carregando pedido..." />;
  if (error) return <ErrorMessage message={error} onRetry={loadOrder} />;
  if (!order) return <ErrorMessage message="Pedido não encontrado" />;

  const currentStatusIndex = STATUS_FLOW.findIndex((s) => s.key === order.status);
  const isCancelled = order.status === 'cancelled';
  const isExpired = order.status === 'expired';
  const isWaitingPayment = order.status === 'payment_pending' && payment?.status === 'pending';

  return (
    <div className="min-h-screen bg-background pb-8" data-testid="order-tracking-page">
      <TopBar title={`Pedido #${order.order_number?.slice(-6)}`} showBack />

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Status Card */}
        <div className="bg-gradient-to-br from-primary to-secondary rounded-2xl p-6 text-white shadow-lg">
          <p className="text-white/80 text-sm">Status atual</p>
          <h2 className="font-['Outfit'] text-2xl font-bold mt-1">
            {STATUS_FLOW.find((s) => s.key === order.status)?.label || order.status}
          </h2>
          {order.estimated_time && !['delivered', 'cancelled', 'expired'].includes(order.status) && (
            <p className="text-white/80 text-sm mt-2">
              ⏱️ Tempo estimado: {order.estimated_time} min
            </p>
          )}
        </div>

        {/* Pix QR */}
        {isWaitingPayment && payment?.pix_code && (
          <div className="bg-white rounded-xl shadow-sm border border-border p-4">
            <h3 className="font-['Outfit'] font-bold text-lg mb-3">Pague com Pix</h3>
            <div className="flex justify-center bg-white p-4 rounded-lg mb-3">
              <QRCode value={payment.pix_code} size={180} />
            </div>
            <button
              data-testid="copy-pix"
              onClick={handleCopyPix}
              className="w-full py-2 bg-muted text-foreground rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-muted/80 transition-colors"
            >
              <Copy className="w-4 h-4" />
              Copiar código Pix
            </button>
            <button
              data-testid="mock-approve-payment"
              onClick={handleMockApprove}
              className="w-full py-2 mt-2 bg-secondary text-secondary-foreground rounded-xl font-semibold hover:opacity-90 transition-opacity"
            >
              ✅ Simular pagamento (DEV)
            </button>
            <p className="text-xs text-muted-foreground text-center mt-2">
              Botão de simulação disponível apenas em ambiente de desenvolvimento
            </p>
          </div>
        )}

        {/* Status Flow */}
        {!isCancelled && !isExpired && (
          <div className="bg-white rounded-xl shadow-sm border border-border p-4">
            <h3 className="font-['Outfit'] font-bold text-lg mb-4">Acompanhamento</h3>
            <div className="space-y-3">
              {STATUS_FLOW.slice(2).map((step, index) => {
                const Icon = step.icon;
                const stepIndex = index + 2;
                const isPast = stepIndex < currentStatusIndex;
                const isCurrent = stepIndex === currentStatusIndex;
                const isActive = isPast || isCurrent;

                return (
                  <div key={step.key} className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isActive
                        ? isCurrent
                          ? 'bg-primary text-primary-foreground animate-pulse'
                          : 'bg-secondary text-secondary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className={`flex-1 ${
                      isActive ? 'font-semibold text-foreground' : 'text-muted-foreground'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Cancelled */}
        {isCancelled && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 flex items-center gap-3">
            <XCircle className="w-8 h-8 text-destructive" />
            <div>
              <p className="font-semibold text-destructive">Pedido cancelado</p>
              <p className="text-sm text-muted-foreground">Entre em contato com o suporte</p>
            </div>
          </div>
        )}

        {/* Items */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-4">
          <h3 className="font-['Outfit'] font-bold text-lg mb-3">Itens do pedido</h3>
          <div className="space-y-2">
            {order.items.map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span>{item.quantity}x {item.product_name}</span>
                <span className="font-semibold">R$ {item.subtotal.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-border mt-3 pt-3 flex justify-between font-bold">
            <span>Total</span>
            <span className="text-primary">R$ {order.total.toFixed(2)}</span>
          </div>
        </div>

        {/* Delivery */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-4">
          <h3 className="font-['Outfit'] font-bold text-lg mb-2">Entrega</h3>
          {order.delivery_location.reference && (
            <p className="text-sm"><strong>Referência:</strong> {order.delivery_location.reference}</p>
          )}
          {order.delivery_location.umbrella_color && (
            <p className="text-sm mt-1"><strong>Cor:</strong> {order.delivery_location.umbrella_color}</p>
          )}
        </div>

        <button
          onClick={() => navigate('/orders')}
          className="w-full py-3 bg-white border border-border text-foreground rounded-xl font-semibold hover:bg-muted transition-colors"
        >
          Ver todos os pedidos
        </button>

        {order.status === 'delivered' && (
          <button
            data-testid="rate-order"
            onClick={() => navigate(`/review/${order.id}`)}
            className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-semibold flex items-center justify-center gap-2 active:scale-95"
          >
            ⭐ Avaliar pedido
          </button>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;
